import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Users, DollarSign, Activity, TrendingUp, Shield, LogOut, RefreshCw, Search, Brain, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import {
  EmotionNetworkGraph,
  EmotionBubbleChart,
  EmotionTimeline,
  EmotionDistribution,
} from '@/components/EmotionVisualizations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#ec4899'];

const SuperAdmin = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [analyses, setAnalyses] = useState([]);
  const [deepAnalytics, setDeepAnalytics] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [filteredAnalyses, setFilteredAnalyses] = useState([]);
  const [supportMessages, setSupportMessages] = useState([]);
  const [supportStats, setSupportStats] = useState(null);

  useEffect(() => {
    const isAuth = sessionStorage.getItem('superadmin_auth') === 'true';
    if (isAuth) {
      setAuthenticated(true);
      loadAllData();
    }
  }, []);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (authenticated && autoRefresh) {
      const interval = setInterval(() => {
        loadAllData();
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [authenticated, autoRefresh]);

  // Search filter effect
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredUsers(users);
      setFilteredAnalyses(analyses);
      return;
    }

    const query = searchQuery.toLowerCase();
    
    // Filter users
    const matchedUsers = users.filter((user) =>
      (user.email && user.email.toLowerCase().includes(query)) ||
      (user.user_id && user.user_id.toLowerCase().includes(query)) ||
      (user.role && user.role.toLowerCase().includes(query))
    );
    
    // Filter analyses
    const matchedAnalyses = analyses.filter((analysis) =>
      (analysis.content_type && analysis.content_type.toLowerCase().includes(query)) ||
      (analysis.dominant_emotion && analysis.dominant_emotion.toLowerCase().includes(query)) ||
      (analysis.content_preview && analysis.content_preview.toLowerCase().includes(query)) ||
      (analysis.custom_question && analysis.custom_question.toLowerCase().includes(query)) ||
      (analysis.user_id && analysis.user_id.toLowerCase().includes(query))
    );
    
    setFilteredUsers(matchedUsers);
    setFilteredAnalyses(matchedAnalyses);
  }, [searchQuery, users, analyses]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === 'SuperAdmin2025!' && secretKey === 'emoq_super_secret_key') {
      sessionStorage.setItem('superadmin_auth', 'true');
      setAuthenticated(true);
      toast.success('Super admin access granted');
      loadAllData();
    } else {
      toast.error('Invalid credentials');
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('superadmin_auth');
    setAuthenticated(false);
    setPassword('');
    setSecretKey('');
  };

  const loadAllData = async () => {
    console.log('Loading all data...');
    try {
      // Load basic stats first
      const statsRes = await axios.get(`${API}/admin/stats`);
      console.log('Stats loaded:', statsRes.data);
      setStats(statsRes.data);

      // Load users
      const usersRes = await axios.get(`${API}/admin/users`);
      console.log('Users loaded:', usersRes.data.users?.length);
      setUsers(usersRes.data.users || []);

      // Load analyses
      const analysesRes = await axios.get(`${API}/admin/analyses`);
      console.log('Analyses loaded:', analysesRes.data.analyses?.length);
      setAnalyses(analysesRes.data.analyses || []);

      // Load deep analytics
      const deepRes = await axios.get(`${API}/admin/analytics/deep`);
      console.log('Deep analytics loaded:', deepRes.data);
      setDeepAnalytics(deepRes.data);

      // Load support messages and stats
      try {
        const [messagesRes, statsRes] = await Promise.all([
          axios.get(`${API}/admin/support/messages`),
          axios.get(`${API}/admin/support/stats`)
        ]);
        console.log('Support messages loaded:', messagesRes.data.total);
        setSupportMessages(messagesRes.data.messages || []);
        setSupportStats(statsRes.data);
      } catch (supportError) {
        console.error('Failed to load support data:', supportError);
        // Don't fail the whole load if support data fails
      }

      toast.success('Data refreshed successfully');
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load some data: ' + (error.response?.data?.detail || error.message));
    }
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <div className="flex items-center justify-center mb-4">
              <Shield className="w-12 h-12 text-purple-600" />
            </div>
            <CardTitle className="text-2xl text-center">Super Admin Access</CardTitle>
            <p className="text-sm text-center text-gray-500">Business Intelligence Dashboard</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="secretKey">Secret Key</Label>
                <Input id="secretKey" type="password" value={secretKey} onChange={(e) => setSecretKey(e.target.value)} required />
              </div>
              <Button type="submit" className="w-full">Access Dashboard</Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  const totalUsers = stats?.total_users || 0;
  const totalAnalyses = stats?.total_analyses || 0;
  const totalRevenue = stats?.total_revenue || 0;
  const activeSubs = stats?.active_subscriptions || 0;
  const newUsersWeek = stats?.new_users_week || 0;
  const newUsersMonth = stats?.new_users_month || 0;

  const chartData = deepAnalytics?.growth_trends || [];
  const contentData = deepAnalytics?.content_analysis?.by_type ? Object.entries(deepAnalytics.content_analysis.by_type).map(([name, value]) => ({ name, value })) : [];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8 text-purple-600" />
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">emoQ.ai Super Admin</h1>
                <p className="text-xs text-gray-500">Live Business Intelligence</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={loadAllData} variant="outline" size="sm">
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button onClick={handleLogout} variant="outline" size="sm">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
          {/* Search Bar */}
          <div className="mt-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search users, analyses, content..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Search Results Indicator */}
        {searchQuery && (
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm font-medium text-blue-900">
              🔍 Search results for "{searchQuery}": {filteredUsers.length} users, {filteredAnalyses.length} analyses
            </p>
            <button
              onClick={() => setSearchQuery('')}
              className="text-xs text-blue-600 underline mt-1"
            >
              Clear search
            </button>
          </div>
        )}

        {/* Last Updated */}
        <div className="text-sm text-gray-500 mb-4 flex items-center justify-between">
          <span>Last updated: {new Date().toLocaleTimeString()}</span>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded"
            />
            <span className="text-xs">Auto-refresh (30s)</span>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white cursor-pointer hover:shadow-lg transition">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Total Users</CardTitle>
              <Users className="w-4 h-4" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{totalUsers}</div>
              <p className="text-xs opacity-80 mt-1">+{newUsersWeek} this week</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white cursor-pointer hover:shadow-lg transition">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Total Analyses</CardTitle>
              <Activity className="w-4 h-4" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{totalAnalyses}</div>
              <p className="text-xs opacity-80 mt-1">{deepAnalytics?.engagement?.active_users_week || 0} active this week</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white cursor-pointer hover:shadow-lg transition">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
              <DollarSign className="w-4 h-4" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">${totalRevenue.toFixed(2)}</div>
              <p className="text-xs opacity-80 mt-1">${(deepAnalytics?.revenue?.this_month || 0).toFixed(2)} this month</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-pink-500 to-pink-600 text-white cursor-pointer hover:shadow-lg transition">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Active Subscriptions</CardTitle>
              <TrendingUp className="w-4 h-4" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{activeSubs}</div>
              <p className="text-xs opacity-80 mt-1">${(deepAnalytics?.overview?.mrr || 0).toFixed(2)} MRR</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList className="grid w-full grid-cols-7">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="users">Users ({filteredUsers.length})</TabsTrigger>
            <TabsTrigger value="analyses">Analyses ({filteredAnalyses.length})</TabsTrigger>
            <TabsTrigger value="emotions">
              <Brain className="w-4 h-4 mr-2" />
              Emotions
            </TabsTrigger>
            <TabsTrigger value="content">Content</TabsTrigger>
            <TabsTrigger value="revenue">Revenue</TabsTrigger>
            <TabsTrigger value="support">
              <MessageSquare className="w-4 h-4 mr-2" />
              Support
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Growth Trends (6 Months)</CardTitle>
                </CardHeader>
                <CardContent>
                  {chartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="users" stroke="#8b5cf6" name="Users" />
                        <Line type="monotone" dataKey="analyses" stroke="#3b82f6" name="Analyses" />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-gray-400">
                      No data available
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Content Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  {contentData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie data={contentData} cx="50%" cy="50%" labelLine={false} label={(entry) => `${entry.name}: ${entry.value}`} outerRadius={80} dataKey="value">
                          {contentData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-gray-400">
                      No data available
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-sm text-gray-600">Retention Rate</p>
                    <p className="text-3xl font-bold text-purple-600">{deepAnalytics?.engagement?.retention_rate || 0}%</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-sm text-gray-600">Avg Analyses/User</p>
                    <p className="text-3xl font-bold text-blue-600">{deepAnalytics?.engagement?.avg_analyses_per_user?.toFixed(1) || 0}</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-sm text-gray-600">New Users (30d)</p>
                    <p className="text-3xl font-bold text-green-600">{newUsersMonth}</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="users" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>All Users ({filteredUsers.length})</CardTitle>
                {searchQuery && (
                  <p className="text-sm text-blue-600">
                    Showing {filteredUsers.length} of {users.length} users
                  </p>
                )}
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3 text-sm font-medium">Email</th>
                        <th className="text-left p-3 text-sm font-medium">Credits</th>
                        <th className="text-left p-3 text-sm font-medium">Admin</th>
                        <th className="text-left p-3 text-sm font-medium">Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.slice(0, 100).map((user, idx) => (
                        <tr key={idx} className="border-b hover:bg-gray-50 cursor-pointer">
                          <td className="p-3 text-sm">{user.email || 'Anonymous'}</td>
                          <td className="p-3 text-sm font-mono">{user.credits || 0}</td>
                          <td className="p-3 text-sm">{user.is_admin ? '✅' : '-'}</td>
                          <td className="p-3 text-sm text-gray-600">
                            {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="analyses" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Recent Analyses ({filteredAnalyses.length})</CardTitle>
                {searchQuery && (
                  <p className="text-sm text-blue-600">
                    Showing {filteredAnalyses.length} of {analyses.length} analyses
                  </p>
                )}
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {filteredAnalyses.slice(0, 50).map((analysis, idx) => (
                    <div key={idx} className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium capitalize">{analysis.content_type || 'Unknown'}</span>
                            {analysis.dominant_emotion && (
                              <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded">
                                {analysis.dominant_emotion}
                              </span>
                            )}
                          </div>
                          {analysis.content_preview && (
                            <p className="text-sm text-gray-600 line-clamp-1">{analysis.content_preview}</p>
                          )}
                          {analysis.custom_question && (
                            <p className="text-xs text-blue-600 mt-1">Q: {analysis.custom_question}</p>
                          )}
                        </div>
                        <span className="text-xs text-gray-500 ml-4">
                          {analysis.timestamp ? new Date(analysis.timestamp).toLocaleDateString() : 'N/A'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="emotions" className="space-y-6">
            {/* Emotion Analytics Header */}
            <Card className="bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 text-white">
              <CardHeader>
                <CardTitle className="text-2xl flex items-center gap-2">
                  <Brain className="w-8 h-8" />
                  Interactive Emotion Analytics
                </CardTitle>
                <p className="text-sm opacity-90">
                  Deep insights into emotional patterns across all analyses • {analyses.length} total analyses • Click & drag to explore
                </p>
              </CardHeader>
            </Card>

            {/* Main Visualizations */}
            <div className="grid grid-cols-1 gap-6">
              {analyses.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <p className="text-gray-500">No analysis data available for visualization</p>
                    <p className="text-sm text-gray-400 mt-2">Analyses will appear here once users start using the platform</p>
                  </CardContent>
                </Card>
              ) : (
                <>
                  <EmotionNetworkGraph analyses={filteredAnalyses.length > 0 ? filteredAnalyses : analyses} />
                  
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <EmotionBubbleChart analyses={filteredAnalyses.length > 0 ? filteredAnalyses : analyses} />
                    <EmotionDistribution analyses={filteredAnalyses.length > 0 ? filteredAnalyses : analyses} />
                  </div>

                  <EmotionTimeline analyses={filteredAnalyses.length > 0 ? filteredAnalyses : analyses} />
                </>
              )}
            </div>

            {/* Emotion Summary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-gray-600">Unique Emotions</p>
                  <p className="text-3xl font-bold text-purple-600">
                    {(() => {
                      const uniqueEmotions = new Set();
                      analyses.forEach(a => {
                        if (a.emotions) {
                          Object.keys(a.emotions).forEach(e => uniqueEmotions.add(e));
                        }
                      });
                      return uniqueEmotions.size;
                    })()}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-gray-600">Most Common Emotion</p>
                  <p className="text-2xl font-bold text-blue-600 capitalize">
                    {(() => {
                      const emotionCounts = {};
                      analyses.forEach(a => {
                        if (a.dominant_emotion) {
                          emotionCounts[a.dominant_emotion] = (emotionCounts[a.dominant_emotion] || 0) + 1;
                        }
                      });
                      const sorted = Object.entries(emotionCounts).sort((a, b) => b[1] - a[1]);
                      return sorted.length > 0 ? sorted[0][0] : 'N/A';
                    })()}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-gray-600">Avg Emotions/Analysis</p>
                  <p className="text-3xl font-bold text-pink-600">
                    {(() => {
                      const totalEmotions = analyses.reduce((sum, a) => {
                        return sum + (a.emotions ? Object.keys(a.emotions).length : 0);
                      }, 0);
                      return analyses.length > 0 ? (totalEmotions / analyses.length).toFixed(1) : 0;
                    })()}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-gray-600">Avg Arousal Level</p>
                  <p className="text-3xl font-bold text-orange-600">
                    {(() => {
                      const totalArousal = analyses.reduce((sum, a) => {
                        return sum + (a.arousal_level || 0);
                      }, 0);
                      return analyses.length > 0 ? (totalArousal / analyses.length).toFixed(1) : 0;
                    })()}
                  </p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="content" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Content Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  {(() => {
                    // Calculate content type breakdown from analyses directly if backend doesn't provide it
                    const contentTypes = {};
                    analyses.forEach(a => {
                      const type = a.content_type || 'unknown';
                      contentTypes[type] = (contentTypes[type] || 0) + 1;
                    });
                    
                    const byType = Object.keys(deepAnalytics?.content_analysis?.by_type || {}).length > 0 
                      ? deepAnalytics.content_analysis.by_type 
                      : contentTypes;
                    
                    return (
                      <div className="space-y-3">
                        {Object.entries(byType).length > 0 ? (
                          Object.entries(byType).map(([type, count]) => (
                            <div key={type} className="flex justify-between items-center p-3 bg-gray-50 rounded hover:bg-gray-100 cursor-pointer transition">
                              <span className="font-medium capitalize">{type}</span>
                              <span className="text-2xl font-bold text-purple-600">{count}</span>
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-8 text-gray-500">
                            No content data available
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Custom Questions ({deepAnalytics?.content_analysis?.custom_questions_count || analyses.filter(a => a.custom_question).length})</CardTitle>
                </CardHeader>
                <CardContent>
                  {(() => {
                    const customQuestions = deepAnalytics?.content_analysis?.custom_questions_sample || 
                      analyses.filter(a => a.custom_question).slice(0, 50);
                    
                    return (
                      <div className="space-y-2 max-h-[400px] overflow-y-auto">
                        {customQuestions.length > 0 ? (
                          customQuestions.map((item, idx) => (
                            <div key={idx} className="p-2 bg-blue-50 rounded text-sm hover:bg-blue-100 cursor-pointer transition">
                              <p className="font-medium text-blue-900">{item.question || item.custom_question}</p>
                              <p className="text-xs text-gray-600 mt-1">
                                {item.content_type} • {item.timestamp ? new Date(item.timestamp).toLocaleDateString() : 'N/A'}
                              </p>
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-8 text-gray-500">
                            No custom questions asked yet
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="revenue" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
                <CardContent className="pt-6">
                  <p className="text-sm opacity-80">Total Revenue</p>
                  <p className="text-4xl font-bold mt-2">${totalRevenue.toFixed(2)}</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                <CardContent className="pt-6">
                  <p className="text-sm opacity-80">MRR</p>
                  <p className="text-4xl font-bold mt-2">${(deepAnalytics?.overview?.mrr || 0).toFixed(2)}</p>
                  <p className="text-xs opacity-80 mt-1">{activeSubs} subscriptions</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
                <CardContent className="pt-6">
                  <p className="text-sm opacity-80">This Month</p>
                  <p className="text-4xl font-bold mt-2">${(deepAnalytics?.revenue?.this_month || 0).toFixed(2)}</p>
                </CardContent>
              </Card>
            </div>

            {chartData.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Revenue Trend</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="revenue" fill="#10b981" name="Revenue ($)" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="support" className="space-y-4">
            <Card className="bg-gradient-to-r from-purple-600 via-blue-600 to-indigo-600 text-white">
              <CardHeader>
                <CardTitle className="text-2xl flex items-center gap-2">
                  <MessageSquare className="w-8 h-8" />
                  Support & Contact Center
                </CardTitle>
                <p className="text-sm opacity-90">
                  User inquiries, privacy requests, and support messages centralized here
                </p>
              </CardHeader>
            </Card>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-gray-600">Total Requests</p>
                  <p className="text-3xl font-bold text-purple-600">{supportStats?.total || 0}</p>
                  <p className="text-xs text-gray-500 mt-1">All time</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-gray-600">Pending</p>
                  <p className="text-3xl font-bold text-orange-600">{supportStats?.by_status?.pending || 0}</p>
                  <p className="text-xs text-gray-500 mt-1">Needs response</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-gray-600">Privacy Requests</p>
                  <p className="text-3xl font-bold text-blue-600">{supportStats?.by_type?.privacy || 0}</p>
                  <p className="text-xs text-gray-500 mt-1">GDPR/CCPA</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-gray-600">Feedback</p>
                  <p className="text-3xl font-bold text-green-600">{supportStats?.by_type?.feedback || 0}</p>
                  <p className="text-xs text-gray-500 mt-1">User feedback</p>
                </CardContent>
              </Card>
            </div>

            {/* Support Messages List */}
            <Card>
              <CardHeader>
                <CardTitle>Support Requests ({supportMessages.length})</CardTitle>
                <p className="text-sm text-gray-600">Most recent requests shown first</p>
              </CardHeader>
              <CardContent>
                {supportMessages.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-semibold mb-2">No Support Requests Yet</p>
                    <p className="text-sm">Messages from users will appear here</p>
                  </div>
                ) : (
                  <div className="space-y-4 max-h-[600px] overflow-y-auto">
                    {supportMessages.map((message, idx) => {
                      const isPrivacy = message.type === 'privacy';
                      const isFeedback = message.type === 'feedback';
                      const isPending = message.status === 'pending';
                      
                      return (
                        <div 
                          key={idx} 
                          className={`p-4 rounded-lg border-2 ${
                            isPending 
                              ? 'bg-orange-50 border-orange-200' 
                              : message.status === 'resolved'
                              ? 'bg-green-50 border-green-200'
                              : 'bg-blue-50 border-blue-200'
                          }`}
                        >
                          {/* Header */}
                          <div className="flex justify-between items-start mb-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-semibold text-gray-900">{message.name}</span>
                                {isPrivacy && (
                                  <span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded">
                                    PRIVACY
                                  </span>
                                )}
                                {isFeedback && (
                                  <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded">
                                    FEEDBACK
                                  </span>
                                )}
                                <span className={`text-xs px-2 py-1 rounded ${
                                  isPending 
                                    ? 'bg-orange-100 text-orange-700' 
                                    : message.status === 'resolved'
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-blue-100 text-blue-700'
                                }`}>
                                  {message.status.toUpperCase()}
                                </span>
                              </div>
                              <p className="text-sm text-gray-600">{message.email}</p>
                              {message.user_id && (
                                <p className="text-xs text-gray-500">User ID: {message.user_id}</p>
                              )}
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-gray-500">
                                {new Date(message.timestamp).toLocaleDateString()}
                              </p>
                              <p className="text-xs text-gray-500">
                                {new Date(message.timestamp).toLocaleTimeString()}
                              </p>
                            </div>
                          </div>

                          {/* Subject */}
                          {message.subject && (
                            <div className="mb-2">
                              <p className="font-semibold text-sm text-gray-900">Subject:</p>
                              <p className="text-sm text-gray-700">{message.subject}</p>
                            </div>
                          )}

                          {/* Message */}
                          <div className="mb-3">
                            <p className="font-semibold text-sm text-gray-900 mb-1">Message:</p>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap">{message.message}</p>
                          </div>

                          {/* Admin Response */}
                          {message.admin_response && (
                            <div className="mt-3 p-3 bg-white rounded border border-gray-200">
                              <p className="font-semibold text-sm text-gray-900 mb-1">Admin Response:</p>
                              <p className="text-sm text-gray-700">{message.admin_response}</p>
                              <p className="text-xs text-gray-500 mt-2">
                                Responded: {new Date(message.responded_at).toLocaleString()}
                              </p>
                            </div>
                          )}

                          {/* Actions */}
                          <div className="mt-3 flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => window.open(`mailto:${message.email}`, '_blank')}
                              className="bg-blue-600 hover:bg-blue-700 text-white"
                            >
                              📧 Reply via Email
                            </Button>
                            {isPending && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  // TODO: Add mark as resolved functionality
                                  toast.info('Marking as resolved...');
                                }}
                              >
                                ✓ Mark Resolved
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* How Users Contact Info */}
            <Card>
              <CardHeader>
                <CardTitle>How Users Contact You</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="p-4 bg-purple-50 rounded-lg">
                  <p className="text-sm text-gray-700 mb-2">
                    Users submit messages through contact forms from:
                  </p>
                  <div className="space-y-1 text-sm text-gray-600">
                    <p>• Footer "Support" button → Opens contact form</p>
                    <p>• Footer "Contact" button → Opens contact form</p>
                    <p>• Footer "Feedback" button → Opens feedback form</p>
                    <p>• Privacy page contact section → Opens privacy request form</p>
                  </div>
                  <p className="text-sm text-gray-700 mt-3">
                    All messages are stored in MongoDB and displayed here. Click "Reply via Email" to respond directly from your email client.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Investor Highlight */}
        <Card className="mt-8 bg-gradient-to-r from-purple-600 to-blue-600 text-white">
          <CardHeader>
            <CardTitle>📊 Key Metrics for Investors</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm opacity-80">Total Users</p>
                <p className="text-3xl font-bold">{totalUsers}</p>
              </div>
              <div>
                <p className="text-sm opacity-80">Monthly Active</p>
                <p className="text-3xl font-bold">{deepAnalytics?.engagement?.active_users_month || 0}</p>
              </div>
              <div>
                <p className="text-sm opacity-80">MRR</p>
                <p className="text-3xl font-bold">${(deepAnalytics?.overview?.mrr || 0).toFixed(0)}</p>
              </div>
              <div>
                <p className="text-sm opacity-80">Engagement</p>
                <p className="text-3xl font-bold">{deepAnalytics?.engagement?.avg_analyses_per_user?.toFixed(1) || 0}x</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SuperAdmin;